import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDeepCompare, useDeepMemo } from '@beeline.com/bp-design-system';
import { useNotificationsIntl } from 'src/components/common/header/translations/useNotificationsIntl';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { NotificationWebSocketMessage } from 'src/components/websockets/Websocket.types';
import { useTypedQuery } from 'src/utils/hooks/useTypedQuery';
import { useTypedMutation } from 'src/utils/hooks/useTypedMutation';
import { filterNotEmpty } from 'src/utils/data/listHelpers';
import { MixpanelEvents } from 'src/integrations/mixpanel/Events/MixpanelEvents';
import { useMixpanel } from 'src/integrations/mixpanel/useMixpanel';
import { MarkNotificationsAsReadDocument } from './utils/__generated__/MarkNotificationsAsRead.graphql';
import { DeleteNotificationDocument } from './utils/__generated__/DeleteNotification.graphql';
import {
  GetUseNotificationsDocument,
  GetUseNotificationsQueryVariables
} from './utils/__generated__/GetUseNotifications.graphql';
import { NotificationsContextProps, Notification } from './Notifications.types';

export const NotificationsContext = createContext<NotificationsContextProps>({
  error: undefined,
  loading: false,
  notifications: [],
  lastNotification: undefined,
  hasPending: false,
  isFullyLoaded: false,
  markNotificationsAsRead: async () => {},
  deleteNotifications: async () => {},
  refetchNotifications: async () => {},
  handleNotificationsWSUpdate: async () => {},
  loadMore: () => {}
});

interface Props {
  userUid: string | undefined;
}

export const NotificationsProvider: FC<React.PropsWithChildren<Props>> = ({ children, userUid }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [page, setPage] = useState<DeeplyDefined<GetUseNotificationsQueryVariables['page']>>({ limit: 100, offset: 0 });
  const [lastNotification, setLastNotification] = useState<Notification | undefined>();
  const { showSuccess, showError } = useSnackbar();
  const intl = useNotificationsIntl();
  const { track } = useMixpanel();

  const {
    loading: getLoading,
    error: getError,
    refetch,
    get: getNotifications
  } = useTypedQuery(GetUseNotificationsDocument, {
    notifyOnNetworkStatusChange: true,
    variables: { page },
    onError: (e) => showError(`${intl.getNotificationError} ${e}`),
    onCompleted: (data) => {
      if (data.notifications.totalCount === null) {
        setIsFullyLoaded(true);
      } else {
        setIsFullyLoaded(data.notifications.totalCount === notifications.length);
      }
      setNotifications((filterNotEmpty(data?.notifications?.notifications) as Notification[]) ?? []);
    }
  });

  const pageHasChanged = useDeepCompare(page);
  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    if (userUid && pageHasChanged) {
      getNotifications();
    }
  }, [userUid, pageHasChanged]);

  const {
    loading: deleteLoading,
    error: deleteError,
    set: deleteNotification
  } = useTypedMutation(DeleteNotificationDocument, {
    onCompleted: () => {
      refetch();
      showSuccess(intl.deleteNotificationSuccess);
    },
    onError: (e) => {
      showError(`${intl.deleteNotificationError} ${e}`);
    }
  });

  const {
    loading: markAsReadLoading,
    error: markAsReadError,
    set: markNotificationsAsRead
  } = useTypedMutation(MarkNotificationsAsReadDocument, {
    onCompleted: () => refetch(),
    onError: (e) => {
      showError(`${intl.markAsReadNotificationError} ${e}`);
    }
  });

  const refetchNotificationsHandler = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const handleNotificationsWSUpdate = useCallback(
    async (webSocketMsgPayload: NotificationWebSocketMessage['payload']) => {
      setLastNotification(webSocketMsgPayload);
      setNotifications((prevState) => {
        const notificationIndex = prevState.findIndex(
          (prevNotification) => webSocketMsgPayload.uid === prevNotification.uid
        );
        if (notificationIndex !== -1) {
          return prevState.map((prevNotification) =>
            webSocketMsgPayload.uid === prevNotification.uid ? webSocketMsgPayload : prevNotification
          );
        }
        return [webSocketMsgPayload, ...prevState];
      });
    },
    []
  );

  const deleteNotificationHandler = useCallback(
    async (uid: string) => {
      await deleteNotification({ variables: { uid } });
    },
    [deleteNotification]
  );

  const loadMoreHandler = useCallback(async () => {
    setPage({ limit: page.limit + 100, offset: page.offset });
  }, [page]);

  const markNotificationsAsReadHandler = useCallback(async () => {
    await markNotificationsAsRead();
    track(MixpanelEvents.notification.CLICK_MARK_ALL_READ);
  }, [markNotificationsAsRead, track]);

  const hasPending = useMemo(() => !!notifications.find((el) => el.status === 'IN_PROGRESS'), [notifications]);

  const value = useDeepMemo(
    () => ({
      deleteNotifications: deleteNotificationHandler,
      markNotificationsAsRead: markNotificationsAsReadHandler,
      refetchNotifications: refetchNotificationsHandler,
      handleNotificationsWSUpdate,
      loadMore: loadMoreHandler,
      lastNotification,
      hasPending,
      notifications,
      isFullyLoaded,
      loading: markAsReadLoading || getLoading || deleteLoading,
      error: getError || markAsReadError || deleteError
    }),
    [
      deleteNotificationHandler,
      markNotificationsAsReadHandler,
      refetchNotificationsHandler,
      handleNotificationsWSUpdate,
      loadMoreHandler,
      lastNotification,
      hasPending,
      notifications,
      isFullyLoaded,
      markAsReadLoading,
      getLoading,
      deleteLoading,
      getError,
      markAsReadError,
      deleteError
    ]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export const useNotificationsContext = () => useContext<NotificationsContextProps>(NotificationsContext);
